import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  BookOpen, 
  Users, 
  MessageCircle, 
  FileText, 
  CheckCircle, 
  ArrowRight,
  Star,
  CreditCard,
  Banknote,
  Mail,
  Phone,
  User,
  Send,
  Download,
  Sparkles,
  Quote,
  Heart
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Por favor completá todos los campos");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/registrations`, formData);
      if (response.status === 201) {
        setRegistrationSuccess(true);
        toast.success("¡Registro exitoso! Te contactaremos pronto.");
        setFormData({ name: "", email: "", phone: "" });
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "Este email ya está registrado");
      } else {
        toast.error("Error al registrarse. Por favor intentá de nuevo.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-[#E6DCD3]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#C47F68] to-[#D4A574] rounded-full flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-serif text-lg md:text-xl text-[#4A4036] font-semibold leading-tight">
                Un espacio propio
              </h1>
              <p className="text-xs text-[#786C60]">experiencias literarias</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('faq')} className="text-[#786C60] hover:text-[#C47F68] transition-colors">
              FAQ
            </button>
            <a 
              href="https://wa.link/ovk6r4" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center text-[#25D366] hover:text-[#1da851] transition-colors"
              data-testid="nav-whatsapp"
            >
              <span className="font-medium text-sm">WhatsApp</span>
              <span className="text-xs text-[#786C60]">¡Conversemos!</span>
            </a>
            <Button 
              onClick={() => scrollToSection('registro')}
              className="bg-[#C47F68] hover:bg-[#B06E58] text-white rounded-full px-6"
              data-testid="nav-register-btn"
            >
              Sumate
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="blob-decoration w-96 h-96 bg-[#C47F68] top-20 -left-48" />
          <div className="blob-decoration w-80 h-80 bg-[#D4A574] bottom-20 right-0" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center relative z-10">
          <div className="animate-fade-in-up">
            <p className="font-serif text-lg md:text-xl text-[#786C60] mb-3 tracking-wide"><span className="font-semibold text-[#C47F68]">Club de lectura</span> Tertulias literarias</p>
            <h1 className="font-serif text-[#4A4036] leading-tight mb-4">
              <span className="block text-5xl md:text-6xl lg:text-7xl mb-6">Creá tu <span className="text-[#C47F68]">ritual</span> de lectura semanal</span>
              <span className="block text-2xl md:text-3xl lg:text-4xl text-[#786C60]">Un espacio de <em className="text-[#C47F68] not-italic font-medium">disfrute</em> y <em className="text-[#C47F68] not-italic font-medium">reflexión literaria,</em></span>
              <span className="block text-2xl md:text-3xl lg:text-4xl text-[#786C60]">en <em className="text-[#C47F68] not-italic font-medium">comunidad</em></span>
            </h1>
            <p className="flex items-center gap-2 text-lg text-[#786C60] mb-8 italic">
              Leer con otros es más lindo <Heart className="w-5 h-5 text-[#C47F68] fill-[#C47F68]" />
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => scrollToSection('registro')}
                className="btn-primary text-lg"
                data-testid="hero-cta-btn"
              >
                Sumate al Club
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => scrollToSection('beneficios')}
                variant="outline"
                className="btn-outline text-lg"
                data-testid="hero-learn-more-btn"
              >
                Conocé más
              </Button>
            </div>
          </div>
          
          <div className="relative animate-fade-in animation-delay-300">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_book-club-landing/artifacts/codhq6an_Aldun_43.jpg"
                alt="Aldana Sol"
                className="w-full h-[500px] object-cover image-hover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#4A4036]/30 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl animate-fade-in-up animation-delay-500">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#C47F68] rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#4A4036]">Comunidad activa</p>
                  <p className="text-sm text-[#786C60]">Encuentros semanales</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NUEVO - Club Liviano */}
      <section className="py-12 px-6 bg-[#F0C75E]/10 border-y-2 border-[#F0C75E]/30" data-testid="club-liviano-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="bg-[#F0C75E] text-[#4A4036] mb-4 px-6 py-2 text-base border-0 font-bold shadow-sm">
              NUEVO!
            </Badge>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#4A4036] mb-2">
              Club Liviano <span className="inline-block">🌿</span>
            </h2>
            <p className="text-[#C47F68] font-medium text-lg">Nos encontramos solo 1 vez en Octubre</p>
            <p className="text-[#786C60] mt-2">Club de lectura sostenible y profundo</p>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-center mb-10">
            <div className="flex gap-6 justify-center items-center">
              <img 
                src="https://customer-assets-v7afamib.emergentagent.net/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/z8on3tj1_image.png"
                alt="Julián López"
                className="w-64 h-64 rounded-full object-cover shadow-lg border-4 border-white"
              />
              <img 
                src="https://customer-assets-v7afamib.emergentagent.net/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/m4usothw_image.png"
                alt="La ilusión de los mamíferos - Julián López"
                className="w-64 rounded-xl shadow-lg"
              />
            </div>
            <div>
              <p className="text-[#786C60] leading-snug mb-4">
                Para lectores con <strong className="text-[#4A4036]">menos tiempo</strong>, pero muchas ganas de leer y compartir literatura, adquirir nuevas herramientas de abordaje y <strong className="text-[#4A4036]">guardarse un ratito especial en su semana dedicado a leer</strong>.
              </p>
              <div className="bg-white rounded-xl p-4 border border-[#F0C75E]/30 mb-4">
                <p className="font-medium text-[#4A4036] mb-1">Tertulia única: Miércoles 21 de Octubre, 18:30 a 20:30 hs</p>
                <p className="text-[#786C60]">Leemos <em className="text-[#4A4036] font-medium">La ilusión de los mamíferos</em>, novela de <strong className="text-[#4A4036]">Julián López</strong></p>
              </div>
              <p className="text-[#786C60] text-sm leading-snug mb-3">
                Dos hombres se encuentran cada domingo en un pequeño departamento de Buenos Aires. Comparten intimidad, conversación y cotidianidad, mientras el resto de la semana cada uno regresa a sus respectivas vidas. Un monólogo introspectivo y nostálgico donde el protagonista rememora una historia de amor que ya llegó a su fin, con Buenos Aires como telón de fondo de la melancolía y el duelo.
              </p>
              <p className="text-[#4A4036] text-sm italic">
                Una novela sobre la complejidad de los vínculos, la intimidad y la imposibilidad de realizar el amor plenamente.
              </p>
            </div>
          </div>

          {/* Pago Club Liviano */}
          <div className="bg-white rounded-2xl p-8 border-2 border-[#F0C75E]/40 shadow-sm max-w-2xl mx-auto text-center">
            <p className="font-serif text-xl text-[#4A4036] mb-2">Venite al Club de lectura liviano</p>
            <div className="text-left max-w-md mx-auto mb-4">
              <p className="text-[#786C60] text-sm mb-2">Tu experiencia incluye:</p>
              <ul className="text-[#786C60] text-sm space-y-1">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#F0C75E] mt-0.5 flex-shrink-0" /> El libro del mes en formato digital</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#F0C75E] mt-0.5 flex-shrink-0" /> Guía de lectura con ejes de análisis</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#F0C75E] mt-0.5 flex-shrink-0" /> 1 tertulia mensual de 2 horas</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-[#F0C75E] mt-0.5 flex-shrink-0" /> Acceso al grupo de WhatsApp del Club</li>
              </ul>
            </div>
            <p className="font-serif text-3xl text-[#4A4036] font-semibold mb-6">$33.000 <span className="text-lg font-normal text-[#786C60]">ARS / mes</span></p>
            <a 
              href="https://mpago.la/16Aw7jS"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#F0C75E] hover:bg-[#E0B74E] text-[#4A4036] font-semibold rounded-full px-8 py-4 text-lg shadow-md transition-all hover:-translate-y-0.5"
              data-testid="club-liviano-pay"
            >
              Suscribite al Club Liviano
            </a>
            <p className="text-xs text-[#786C60] mt-4">Al pagar, ingresás al Club liviano por octubre. Luego, evaluás si te quedás.</p>
            <p className="text-[#C47F68] font-medium mt-4 text-sm">Entrá ahora y te asegurás de leer, reflexionar y compartir en Octubre</p>
          </div>
        </div>
      </section>

      {/* En qué consiste Section */}
      <section id="beneficios" className="section-padding bg-[#F5F0EB]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#4A4036] mb-8">¿En qué consiste la experiencia?</h2>
              <p className="text-lg text-[#786C60] mb-8">Leemos y analizamos <span className="text-[#C47F68] font-medium">literatura</span> de manera colectiva. El corazón del Club es el <strong className="text-[#4A4036]">compartir</strong>.</p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-1 h-full min-h-[60px] bg-[#C47F68] rounded-full flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-serif text-lg text-[#4A4036] font-medium mb-1">Lectura compartida</p>
                    <p className="text-[#786C60] text-sm">En cada tertulia creamos una lectura que es <em className="text-[#4A4036]">mucho más</em> que las individuales: más robusta, enriquecida, que nos cambia la mirada.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-1 h-full min-h-[60px] bg-[#D4A574] rounded-full flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-serif text-lg text-[#4A4036] font-medium mb-1">Guía de lectura</p>
                    <p className="text-[#786C60] text-sm">Cada mes recibís una guía con <strong className="text-[#4A4036]">ejes y preguntas disparadoras</strong> para saber en qué poner foco y abrir caminos de interpretación.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-1 h-full min-h-[60px] bg-[#7FBDC5] rounded-full flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-serif text-lg text-[#4A4036] font-medium mb-1">Acompañamiento</p>
                    <p className="text-[#786C60] text-sm">No estás sola: tenés mi <strong className="text-[#4A4036]">guía y contextualización</strong> en cada tertulia. Respondo todas las dudas, sobre todo en lecturas exigentes.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_book-club-landing/artifacts/s25k5kz7_Aldun_78.jpg"
                alt="Lectora disfrutando con mate"
                className="rounded-2xl shadow-xl w-full h-[480px] object-cover object-center image-hover"
              />
            </div>
          </div>

          {/* Qué vas a lograr */}
          <div className="text-center mb-12 mt-4">
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A4036]">
              ¿Qué vas a <span className="text-[#C47F68]">lograr</span> al ser parte del Club?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="card-soft group" data-testid="benefit-card-0">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#C47F68]/10 rounded-2xl flex items-center justify-center text-[#C47F68] mb-6 group-hover:bg-[#C47F68] group-hover:text-white transition-colors duration-300">
                  <Star className="w-8 h-8" />
                </div>
                <h3 className="font-serif text-xl text-[#4A4036] mb-3">Tiempo para vos</h3>
                <p className="text-[#786C60] leading-relaxed">
                  Unas horas a la semana <strong className="text-[#4A4036]">para vos</strong>, para tus intereses. <strong className="text-[#4A4036]">Lecturas pre-seleccionadas</strong> con una mirada cuidada, sin necesidad de investigar ni dudar.
                </p>
              </CardContent>
            </Card>

            <Card className="card-soft group" data-testid="benefit-card-1">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#C47F68]/10 rounded-2xl flex items-center justify-center text-[#C47F68] mb-6 group-hover:bg-[#C47F68] group-hover:text-white transition-colors duration-300">
                  <Users className="w-8 h-8" />
                </div>
                <h3 className="font-serif text-xl text-[#4A4036] mb-3">Comunidad y debate</h3>
                <p className="text-[#786C60] leading-relaxed">
                  Una <strong className="text-[#4A4036]">comunidad lectora</strong> que sostiene el hábito y el placer de leer. <strong className="text-[#4A4036]">Tertulias de charla y reflexión</strong> para profundizar en el análisis y la crítica literaria.
                </p>
              </CardContent>
            </Card>

            <Card className="card-soft group" data-testid="benefit-card-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#C47F68]/10 rounded-2xl flex items-center justify-center text-[#C47F68] mb-6 group-hover:bg-[#C47F68] group-hover:text-white transition-colors duration-300">
                  <FileText className="w-8 h-8" />
                </div>
                <h3 className="font-serif text-xl text-[#4A4036] mb-3">Guías y material</h3>
                <p className="text-[#786C60] leading-relaxed">
                  <strong className="text-[#4A4036]">Guías de lectura</strong> que disparan reflexiones y abren caminos posibles. Participás en el <strong className="text-[#4A4036]">proceso de selección</strong> de autores y obras de calidad.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* LECTURA ACTUAL - Julián López */}
      <section className="section-padding bg-gradient-to-b from-[#FDFBF7] to-[#F5F0EB]" data-testid="current-reading-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#C47F68] text-white mb-4 px-6 py-2 text-base border-0">
              Lectura del mes
            </Badge>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#4A4036] mb-4">
              Ahora estamos leyendo a
            </h2>
            <h3 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#C47F68] mb-8">
              Julián López
            </h3>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div className="flex gap-8 justify-center items-center">
              <img 
                src="https://customer-assets-v7afamib.emergentagent.net/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/z8on3tj1_image.png"
                alt="Julián López"
                className="w-64 h-64 rounded-full object-cover shadow-2xl border-4 border-white flex-shrink-0"
              />
              <img 
                src="https://customer-assets-v7afamib.emergentagent.net/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/m4usothw_image.png"
                alt="La ilusión de los mamíferos"
                className="w-56 rounded-xl shadow-lg hidden md:block"
              />
            </div>
            <div>
              <p className="text-lg text-[#786C60] leading-snug mb-6">
                Escritor argentino. <em className="text-[#4A4036]">La ilusión de los mamíferos</em> narra el encuentro semanal de dos hombres en un pequeño departamento de Buenos Aires: cada domingo comparten sexo, conversación y cotidianidad, mientras el resto de la semana cada uno regresa a sus respectivas vidas. Un monólogo introspectivo y nostálgico sobre una historia de amor que ya llegó a su fin, con la ciudad como telón de fondo de la melancolía y el duelo.
              </p>
              <p className="text-[#4A4036] leading-snug italic">
                Una novela sobre la complejidad de los vínculos, la intimidad y la imposibilidad de realizar el amor plenamente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEPTIEMBRE - Ya leímos - Mónica Ojeda */}
      <section className="section-padding" data-testid="september-reading-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="bg-[#786C60] text-white mb-4 px-4 py-1">
              Septiembre 2026 - Ya leímos
            </Badge>
            <h3 className="font-serif text-3xl md:text-4xl text-[#C47F68]">
              Mónica Ojeda
            </h3>
          </div>
          <div className="flex flex-col md:flex-row gap-10 items-center justify-center">
            <img 
              src="https://customer-assets-v7afamib.emergentagent.net/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/8pyvso1j_image.png"
              alt="Mónica Ojeda"
              className="w-44 h-44 rounded-full object-cover shadow-lg border-4 border-white"
            />
            <img 
              src="https://customer-assets-v7afamib.emergentagent.net/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/o8a9kj68_2.png"
              alt="Las voladoras"
              className="w-36 rounded-xl shadow-lg"
            />
            <div className="max-w-md text-center md:text-left">
              <p className="text-lg text-[#786C60]">
                <em className="font-serif text-xl text-[#4A4036]">Las voladoras</em> — Ocho relatos que exploran el gótico andino, donde la violencia, el misticismo y lo poético convergen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AGOSTO - Lectura pasada - Lucía Solla Sobral */}
      <section className="section-padding" data-testid="august-reading-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="bg-[#786C60] text-white mb-4 px-4 py-1">
              Agosto 2026 - Ya leímos
            </Badge>
            <h3 className="font-serif text-3xl md:text-4xl text-[#C47F68]">
              Lucía Solla Sobral
            </h3>
          </div>
          <div className="flex flex-col md:flex-row gap-10 items-center justify-center">
            <img 
              src="https://customer-assets-v7afamib.emergentagent.net/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/lxavnjow_image.webp"
              alt="Lucía Solla Sobral"
              className="w-44 h-44 rounded-full object-cover shadow-lg border-4 border-white"
            />
            <img 
              src="https://customer-assets-v7afamib.emergentagent.net/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/msbe5m3b_image.png"
              alt="Comerás flores"
              className="w-36 rounded-xl shadow-lg"
            />
            <div className="max-w-md text-center md:text-left">
              <p className="text-lg text-[#786C60]">
                <em className="font-serif text-xl text-[#4A4036]">Comerás flores</em> — Su primera novela, ganadora del Premio Ojo Crítico de Narrativa 2025.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LÍNEA TEMPORAL HORIZONTAL - Recorrido literario */}
      <section className="py-16 px-6" data-testid="timeline-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-[#4A4036] mb-3">
              Nuestro <span className="text-[#C47F68]">recorrido</span> literario
            </h2>
            <p className="text-[#786C60]">Cada mes una nueva autora, una nueva experiencia</p>
          </div>

          <div className="overflow-x-auto pb-4">
            <div className="min-w-[1500px]">
              {/* Fotos de autoras */}
              <div className="flex items-center justify-between mb-6 px-4">
                <div className="flex-1 flex flex-col items-center">
                  <img src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/1o1h4zij_kamiya.jpg" alt="Alejandra Kamiya" className="w-28 h-28 rounded-full object-cover shadow-lg border-4 border-white" />
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <img src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/5uapvk22_sami%202.jpg" alt="Samanta Schweblin" className="w-28 h-28 rounded-full object-cover shadow-lg border-4 border-white" />
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <img src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/imuvxvnu_image.png" alt="Silvina Ocampo" className="w-28 h-28 rounded-full object-cover shadow-lg border-4 border-white" />
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <img src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/7z4do65d_image.png" alt="Elena Garro" className="w-28 h-28 rounded-full object-cover shadow-lg border-4 border-white" />
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <img src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/udjdgd3i_image.png" alt="Clarice Lispector" className="w-28 h-28 rounded-full object-cover shadow-lg border-4 border-white" />
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <img src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/9603p9s7_image.png" alt="Sofía Balbuena" className="w-28 h-28 rounded-full object-cover shadow-lg border-4 border-white" />
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <img src="https://customer-assets-v7afamib.emergentagent.net/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/lxavnjow_image.webp" alt="Lucía Solla Sobral" className="w-28 h-28 rounded-full object-cover shadow-lg border-4 border-white" />
                </div>
                <div className="flex-1 flex flex-col items-center">
                  <img src="https://customer-assets-v7afamib.emergentagent.net/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/8pyvso1j_image.png" alt="Mónica Ojeda" className="w-28 h-28 rounded-full object-cover shadow-lg border-4 border-white" />
                </div>
              </div>

              {/* Línea con puntos */}
              <div className="flex items-center justify-between px-4 mb-6">
                <div className="flex-1 flex justify-center"><div className="w-4 h-4 bg-[#E6DCD3] rounded-full border-2 border-[#D4A574]" /></div>
                <div className="flex-1 h-0.5 bg-gradient-to-r from-[#E6DCD3] to-[#D4A574] -mx-4" />
                <div className="flex-1 flex justify-center"><div className="w-4 h-4 bg-[#D4A574] rounded-full border-2 border-[#C47F68]" /></div>
                <div className="flex-1 h-0.5 bg-[#D4A574] -mx-4" />
                <div className="flex-1 flex justify-center"><div className="w-4 h-4 bg-[#D4A574] rounded-full border-2 border-[#C47F68]" /></div>
                <div className="flex-1 h-0.5 bg-gradient-to-r from-[#D4A574] to-[#C47F68] -mx-4" />
                <div className="flex-1 flex justify-center"><div className="w-4 h-4 bg-[#C47F68] rounded-full border-2 border-[#C47F68]" /></div>
                <div className="flex-1 h-0.5 bg-[#C47F68] -mx-4" />
                <div className="flex-1 flex justify-center"><div className="w-4 h-4 bg-[#C47F68] rounded-full border-2 border-[#C47F68]" /></div>
                <div className="flex-1 h-0.5 bg-[#C47F68] -mx-4" />
                <div className="flex-1 flex justify-center"><div className="w-4 h-4 bg-[#C47F68] rounded-full border-2 border-[#C47F68]" /></div>
                <div className="flex-1 h-0.5 bg-[#C47F68] -mx-4" />
                <div className="flex-1 flex justify-center"><div className="w-4 h-4 bg-[#C47F68] rounded-full border-2 border-[#C47F68]" /></div>
                <div className="flex-1 h-0.5 bg-[#C47F68] -mx-4" />
                <div className="flex-1 flex justify-center"><div className="w-4 h-4 bg-[#C47F68] rounded-full border-2 border-[#C47F68]" /></div>
              </div>

              {/* Meses, nombres y libros */}
              <div className="flex items-start justify-between px-4">
                <div className="flex-1 text-center px-1">
                  <p className="text-sm font-medium text-[#786C60] uppercase tracking-wider">Febrero</p>
                  <p className="font-serif text-lg text-[#C47F68] font-medium mt-1">Alejandra Kamiya</p>
                  <p className="text-xs text-[#786C60] mt-1 italic">La paciencia del agua sobre cada piedra</p>
                </div>
                <div className="flex-1 text-center px-1">
                  <p className="text-sm font-medium text-[#786C60] uppercase tracking-wider">Marzo</p>
                  <p className="font-serif text-lg text-[#C47F68] font-medium mt-1">Samanta Schweblin</p>
                  <p className="text-xs text-[#786C60] mt-1 italic">Kentukis · El buen mal</p>
                </div>
                <div className="flex-1 text-center px-1">
                  <p className="text-sm font-medium text-[#786C60] uppercase tracking-wider">Abril</p>
                  <p className="font-serif text-lg text-[#C47F68] font-medium mt-1">Silvina Ocampo</p>
                  <p className="text-xs text-[#786C60] mt-1 italic">Viaje olvidado</p>
                </div>
                <div className="flex-1 text-center px-1">
                  <p className="text-sm font-medium text-[#786C60] uppercase tracking-wider">Mayo</p>
                  <p className="font-serif text-lg text-[#C47F68] font-medium mt-1">Elena Garro</p>
                  <p className="text-xs text-[#786C60] mt-1 italic">La semana de colores</p>
                </div>
                <div className="flex-1 text-center px-1">
                  <p className="text-sm font-medium text-[#786C60] uppercase tracking-wider">Junio</p>
                  <p className="font-serif text-lg text-[#C47F68] font-medium mt-1">Clarice Lispector</p>
                  <p className="text-xs text-[#786C60] mt-1 italic">Lazos de familia</p>
                </div>
                <div className="flex-1 text-center px-1">
                  <p className="text-sm font-medium text-[#786C60] uppercase tracking-wider">Julio</p>
                  <p className="font-serif text-lg text-[#C47F68] font-medium mt-1">Sofía Balbuena</p>
                  <p className="text-xs text-[#786C60] mt-1 italic">Personaje secundario</p>
                </div>
                <div className="flex-1 text-center px-1">
                  <p className="text-sm font-medium text-[#786C60] uppercase tracking-wider">Agosto</p>
                  <p className="font-serif text-lg text-[#C47F68] font-medium mt-1">Lucía Solla Sobral</p>
                  <p className="text-xs text-[#786C60] mt-1 italic">Comerás flores</p>
                </div>
                <div className="flex-1 text-center px-1">
                  <p className="text-sm font-medium text-[#786C60] uppercase tracking-wider">Septiembre</p>
                  <p className="font-serif text-lg text-[#C47F68] font-medium mt-1">Mónica Ojeda</p>
                  <p className="text-xs text-[#786C60] mt-1 italic">Las voladoras</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qué incluye Section */}
      <section className="section-padding bg-[#F5F0EB]" data-testid="includes-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A4036] mb-4">¿Qué incluye el Club?</h2>
            <p className="text-lg text-[#786C60]">Todo lo que necesitás para una experiencia literaria completa</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="card-soft text-center" data-testid="includes-whatsapp">
              <CardContent className="p-8">
                <div className="w-20 h-20 bg-[#25D366]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MessageCircle className="w-10 h-10 text-[#25D366]" />
                </div>
                <h3 className="font-serif text-2xl text-[#4A4036] mb-4">Comunidad de WhatsApp</h3>
                <p className="text-[#786C60]">
                  Estaremos activos en un grupo de WhatsApp por donde se envía todo el material, los links de los encuentros y se intercambia entre tertulias.
                </p>
              </CardContent>
            </Card>
            
            <Card className="card-soft text-center" data-testid="includes-tertulias">
              <CardContent className="p-8">
                <div className="w-20 h-20 bg-[#C47F68]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-10 h-10 text-[#C47F68]" />
                </div>
                <h3 className="font-serif text-2xl text-[#4A4036] mb-4">Tertulias Mensuales</h3>
                <p className="text-[#786C60]">
                  Encuentros de charla, debate y reflexión. El Club tiene 3 tertulias por mes, y el Club Liviano 1 tertulia mensual.
                </p>
              </CardContent>
            </Card>
            
            <Card className="card-soft text-center" data-testid="includes-guides">
              <CardContent className="p-8">
                <div className="w-20 h-20 bg-[#7FBDC5]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FileText className="w-10 h-10 text-[#7FBDC5]" />
                </div>
                <h3 className="font-serif text-2xl text-[#4A4036] mb-4">Guías de Lectura</h3>
                <p className="text-[#786C60]">
                  Cada lectura viene con su guía, con ejes de análisis que abren caminos de interpretación y entrenan el ojo crítico.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonios Section */}
      <section className="section-padding" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#7FBDC5] text-white mb-4 px-4 py-1.5">
              Lo que dicen nuestras lectoras
            </Badge>
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A4036] mb-4">Testimonios</h2>
            <p className="text-lg text-[#786C60] max-w-2xl mx-auto">
              Las guías y los ejes de análisis hacen la diferencia
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Testimonio Lectora 1 - Parte 1 */}
            <Card className="bg-white rounded-2xl border border-[#E6DCD3] overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-[#C47F68]/10 px-6 py-5 border-b border-[#E6DCD3]">
                  <p className="text-[#C47F68] font-serif text-lg md:text-xl font-semibold italic">
                    "La riqueza de las distintas lecturas fue lo más lindo"
                  </p>
                </div>
                <div className="p-6">
                  <Quote className="w-8 h-8 text-[#E6DCD3] mb-4" />
                  <p className="text-[#786C60] leading-relaxed mb-4">
                    El Club superó mis expectativas, la riqueza de las distintas lecturas, desde las distintas experiencias y lugares de los que viene cada integrante del grupo fue lo más lindo del proceso. También <strong className="text-[#4A4036]">el intercambio ameno donde todas las voces tuvieron lugar</strong> y se fueron entretejiendo unas con otras.
                  </p>
                  <p className="text-[#786C60] leading-relaxed mb-4 text-sm">
                    La elección del libro y autora elegida por Aldi fue buenísima. <em>La paciencia del agua sobre cada piedra</em> me queda asociada a una gratísima experiencia de lectura colectiva.
                  </p>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-5 h-5 fill-[#F59E0B] text-[#F59E0B]" />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonio Lectora 1 - Parte 2 */}
            <Card className="bg-white rounded-2xl border border-[#E6DCD3] overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-[#C47F68]/10 px-6 py-5 border-b border-[#E6DCD3]">
                  <p className="text-[#C47F68] font-serif text-lg md:text-xl font-semibold italic">
                    "La amorosidad y calidez de Aldana"
                  </p>
                </div>
                <div className="p-6">
                  <Quote className="w-8 h-8 text-[#E6DCD3] mb-4" />
                  <p className="text-[#786C60] leading-relaxed mb-4">
                    Lo que más me gustó del Club fue la amorosidad y calidez de Aldana, la propuesta de recorrer el libro en otro orden, <strong className="text-[#4A4036]">las guías que acompañan la lectura</strong>. El grupo de compas que me tocó y sus lecturas super interesantes.
                  </p>
                  <p className="text-[#786C60] leading-relaxed mb-4 text-sm">
                    Había un compromiso de todas por llegar con al menos una lectura de cada cuento y eso es lindo también. Y a la vez la charla era amena y relajada. Lo recomiendo mucho.
                  </p>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-5 h-5 fill-[#F59E0B] text-[#F59E0B]" />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonio Lectora 2 - Parte 1 */}
            <Card className="bg-white rounded-2xl border border-[#E6DCD3] overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-[#C47F68]/10 px-6 py-5 border-b border-[#E6DCD3]">
                  <p className="text-[#C47F68] font-serif text-lg md:text-xl font-semibold italic">
                    "Un espacio donde solo pensaba de forma poética"
                  </p>
                </div>
                <div className="p-6">
                  <Quote className="w-8 h-8 text-[#E6DCD3] mb-4" />
                  <p className="text-[#786C60] leading-relaxed mb-4">
                    Cerré el mes en el Club con mucha gratitud al espacio, la guía y las compañeras. Fue un espacio donde por un rato solo pensaba de forma poética y reflexiva. <strong className="text-[#4A4036]">Un placer hasta necesario en los tiempos que corren.</strong>
                  </p>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-5 h-5 fill-[#F59E0B] text-[#F59E0B]" />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonio Lectora 2 - Parte 2 */}
            <Card className="bg-white rounded-2xl border border-[#E6DCD3] overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-[#C47F68]/10 px-6 py-5 border-b border-[#E6DCD3]">
                  <p className="text-[#C47F68] font-serif text-lg md:text-xl font-semibold italic">
                    "Sorprenderme de mi misma y de aspectos que otros encontraron"
                  </p>
                </div>
                <div className="p-6">
                  <Quote className="w-8 h-8 text-[#E6DCD3] mb-4" />
                  <p className="text-[#786C60] leading-relaxed mb-4">
                    Lo que más valoro son las reflexiones y lecturas propias y ajenas. Sorprenderme de mi misma y de aspectos que otros encontraron y en los que yo no había reparado. <strong className="text-[#4A4036]">Lo recomiendo sin dudas y voy a volver!</strong>
                  </p>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-5 h-5 fill-[#F59E0B] text-[#F59E0B]" />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Audio Testimonial */}
      <section className="py-12 px-6" data-testid="audio-testimonial">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E6DCD3]">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-[#C47F68]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Quote className="w-6 h-6 text-[#C47F68]" />
              </div>
              <div>
                <p className="font-serif text-lg text-[#4A4036] font-medium">Escuchá a Alicia</p>
                <p className="text-sm text-[#786C60]">Lectora del Club, de primera mano</p>
              </div>
            </div>
            <audio 
              controls 
              className="w-full"
              data-testid="alicia-audio"
            >
              <source src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/rkwr3poz_WhatsApp%20Ptt%202026-05-29%20at%2020.27.45.ogg" type="audio/ogg" />
              Tu navegador no soporta el elemento de audio.
            </audio>
          </div>
        </div>
      </section>

      {/* Galería de Tertulias */}
      <section className="section-padding bg-[#F5F0EB]" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A4036] mb-3">
              Así se viven nuestras <span className="text-[#C47F68]">tertulias</span>
            </h2>
            <p className="text-lg text-[#786C60]">Momentos reales de encuentro, lectura y comunidad</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="rounded-2xl overflow-hidden shadow-md">
              <img 
                src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/onmp56sv_Captura%20de%20pantalla%202026-02-25%20a%20la%28s%29%208.14.35%E2%80%AFp.%C2%A0m..png"
                alt="Tertulia - lectoras mostrando sus libros"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md">
              <img 
                src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/10wcx77w_Captura%20de%20pantalla%202026-02-11%20a%20la%28s%29%207.09.34%E2%80%AFp.%C2%A0m..png"
                alt="Tertulia - grupo en videollamada"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md">
              <img 
                src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/c0t2sbaj_Captura%20de%20pantalla%202026-03-27%20a%20la%28s%29%208.10.04%E2%80%AFp.%C2%A0m..png"
                alt="Tertulia - Samanta Schweblin"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md">
              <img 
                src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/9rd8o4ua_Captura%20de%20pantalla%202026-03-11%20a%20la%28s%29%208.18.02%E2%80%AFp.%C2%A0m..png"
                alt="Tertulia - encuentro grupal"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md">
              <img 
                src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/ofdjyj54_Captura%20de%20pantalla%202026-03-15%20a%20la%28s%29%203.35.45%E2%80%AFp.%C2%A0m..png"
                alt="Tertulia - grupo grande"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-md">
              <img 
                src="https://customer-assets.emergentagent.com/job_7ef7c8fe-29af-4b4f-8eae-1b35132c8a22/artifacts/yhl5y0pf_Captura%20de%20pantalla%202026-05-18%20a%20la%28s%29%202.46.31%E2%80%AFp.%C2%A0m..png"
                alt="Tertulia - primeros encuentros"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sobre la Facilitadora */}
      <section className="section-padding bg-[#F5F0EB]" data-testid="facilitator-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_book-club-landing/artifacts/p8xybvaj_Aldun_11.jpg"
                alt="Aldana Sol"
                className="rounded-2xl shadow-xl w-full image-hover"
              />
            </div>
            <div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#4A4036] mb-6">
                ¡Hola! Soy Aldana Sol
              </h2>
              <p className="text-lg text-[#786C60] leading-relaxed mb-6">
                Lectora y amante de la literatura en todas sus formas. Profesora en Letras por la UBA, y Maestranda en Estudios Literarios (UBA - FFyL).
              </p>
              <p className="text-lg text-[#786C60] leading-relaxed mb-8">
                Me entusiasma armar un círculo de lectores que disfruten de leer y quieran profundizar. <strong>No es necesario que tengas experiencia previa</strong> en estudios sobre literatura o talleres. ¡Solo las ganas de ser parte de una comunidad lectora!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tu Camino en el Club - Visual Journey */}
      <section id="journey" className="section-padding" data-testid="journey-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#7FBDC5] text-white mb-4 px-4 py-1.5">
              Tu experiencia
            </Badge>
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A4036] mb-4">¿Qué pasa cuando te suscribís?</h2>
            <p className="text-lg text-[#786C60] max-w-2xl mx-auto">
              Este es el camino que vas a recorrer una vez que te sumes al club
            </p>
          </div>
          
          <div className="relative">
            {/* Connection line for desktop */}
            <div className="hidden lg:block absolute top-24 left-[10%] right-[10%] h-1 bg-gradient-to-r from-[#C47F68] via-[#D4A574] to-[#C47F68] rounded-full" />
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  step: 1,
                  icon: <Send className="w-8 h-8" />,
                  title: "Te registrás",
                  description: "Completás el formulario con tus datos y elegís tu forma de pago preferida.",
                  color: "bg-[#C47F68]"
                },
                {
                  step: 2,
                  icon: <CreditCard className="w-8 h-8" />,
                  title: "Realizás el pago",
                  description: "Transferencia, MercadoPago o PayPal. Te enviamos los datos por email.",
                  color: "bg-[#D4A574]"
                },
                {
                  step: 3,
                  icon: <MessageCircle className="w-8 h-8" />,
                  title: "Entrás al grupo",
                  description: "Te sumamos al grupo de WhatsApp donde está toda la comunidad activa.",
                  color: "bg-[#C47F68]"
                },
                {
                  step: 4,
                  icon: <Download className="w-8 h-8" />,
                  title: "Recibís el material",
                  description: "Te enviamos el texto en formato digital y la guía de lectura del mes.",
                  color: "bg-[#D4A574]"
                }
              ].map((item, index) => (
                <div key={`step-${item.step}`} className="relative" data-testid={`journey-step-${item.step}`}>
                  <div className="journey-step text-center h-full">
                    {/* Step number */}
                    <div className={`w-16 h-16 ${item.color} rounded-full flex items-center justify-center mx-auto mb-6 text-white shadow-lg relative z-10`}>
                      {item.icon}
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-[#C47F68] text-[#C47F68] font-bold text-sm">
                      {item.step}
                    </div>
                    <h3 className="font-serif text-xl text-[#4A4036] mb-3">{item.title}</h3>
                    <p className="text-[#786C60] text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Final step highlight */}
            <div className="mt-12 bg-white rounded-2xl p-8 border-2 border-[#C47F68] shadow-lg">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-20 h-20 bg-[#C47F68] rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-10 h-10 text-white" />
                </div>
                <div className="text-center md:text-left flex-1">
                  <h3 className="font-serif text-2xl text-[#4A4036] mb-2">¡Y empieza la magia!</h3>
                  <p className="text-[#786C60]">
                    Participás de las tertulias, compartís tus reflexiones en el grupo, y disfrutás de leer en comunidad. 
                    Cada mes elegimos juntas la próxima lectura.
                  </p>
                </div>
                <Button 
                  onClick={() => scrollToSection('registro')}
                  className="btn-primary flex-shrink-0"
                >
                  Empezar ahora
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Precios Section */}
      <section id="precios" className="section-padding bg-[#F5F0EB]" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A4036] mb-4">Accesos</h2>
            <p className="text-lg text-[#786C60] mb-6">Elegí el plan que mejor se adapte a vos</p>
            
            {/* Grupos destacados */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch max-w-3xl mx-auto mb-6" data-testid="groups-display">
              {/* Club - Viernes */}
              <div className="flex-1 relative bg-gradient-to-br from-[#4A4036] to-[#6B5D52] rounded-2xl p-6 text-white shadow-lg overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8" />
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-6 -translate-x-6" />
                <div className="relative z-10">
                  <p className="text-white/60 text-sm font-medium uppercase tracking-wider mb-1">Club · 3 tertulias/mes</p>
                  <p className="font-serif text-2xl md:text-3xl font-bold mb-2">Viernes</p>
                  <p className="text-lg text-white/70">18:30 a 20 hs</p>
                  <Badge className="mt-3 bg-white text-[#4A4036] border-0 font-semibold px-3 py-1">
                    2 cupos disponibles
                  </Badge>
                </div>
              </div>

              {/* Club Liviano */}
              <div className="flex-1 relative bg-gradient-to-br from-[#F0C75E] to-[#E8B84D] rounded-2xl p-6 text-[#4A4036] shadow-lg overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/20 rounded-full -translate-y-8 translate-x-8" />
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/20 rounded-full translate-y-6 -translate-x-6" />
                <div className="relative z-10">
                  <p className="text-[#4A4036]/70 text-sm font-medium uppercase tracking-wider mb-1">Club Liviano · 1 tertulia/mes</p>
                  <p className="font-serif text-2xl md:text-3xl font-bold mb-2">Miércoles</p>
                  <p className="text-lg text-[#4A4036]/80">21 de Octubre · 18:30 hs</p>
                  <Badge className="mt-3 bg-white text-[#4A4036] border-0 font-semibold px-3 py-1">
                    NUEVO
                  </Badge>
                </div>
              </div>
            </div>

            <p className="font-serif text-2xl md:text-3xl text-[#C47F68] font-semibold mt-2">Precios del Club — Grupo Viernes</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Plan Mensual */}
            <Card className="price-card" data-testid="price-card-monthly">
              <CardHeader className="pb-4">
                <CardTitle className="font-serif text-2xl text-[#4A4036]">Por un mes</CardTitle>
                <p className="text-[#786C60]">Julio 2026</p>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="font-serif text-4xl text-[#4A4036]">$60.000</span>
                    <span className="text-[#786C60]">ARS</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif text-2xl text-[#786C60]">$50</span>
                    <span className="text-[#786C60]">USD</span>
                  </div>
                </div>
                
                <div className="space-y-3 mb-8">
                  <p className="font-semibold text-[#4A4036] mb-2">Incluye:</p>
                  {[
                    "3 tertulias literarias mensuales",
                    "Guías de lectura con ejes de análisis",
                    "Texto del mes en formato digital",
                    "Comunidad de WhatsApp activa"
                  ].map((feature) => (
                    <div key={feature} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#7FBDC5] flex-shrink-0 mt-0.5" />
                      <span className="text-[#786C60]">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Button 
                  onClick={() => scrollToSection('registro')}
                  className="w-full btn-secondary"
                  data-testid="price-monthly-btn"
                >
                  Elegir plan mensual
                </Button>
              </CardContent>
            </Card>

            {/* Plan Trimestral */}
            <Card className="price-card featured" data-testid="price-card-quarterly">
              <CardHeader className="pb-4">
                <CardTitle className="font-serif text-2xl text-[#4A4036]">Por tres meses</CardTitle>
                <p className="text-[#786C60]">Julio - Septiembre 2026</p>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-serif text-4xl text-[#4A4036]">$144.000</span>
                    <span className="text-[#786C60]">ARS</span>
                  </div>
                  <p className="text-sm text-[#C47F68] font-medium mb-2">$48.000 por mes - ¡Ahorrás $36.000!</p>
                  <div className="flex items-baseline gap-2">
                    <span className="font-serif text-2xl text-[#786C60]">$120</span>
                    <span className="text-[#786C60]">USD</span>
                  </div>
                </div>
                
                <div className="space-y-3 mb-8">
                  <p className="font-semibold text-[#4A4036] mb-2">Incluye:</p>
                  {[
                    "3 tertulias literarias mensuales",
                    "Guías de lectura con ejes de análisis",
                    "Texto del mes en formato digital",
                    "Comunidad de WhatsApp activa",
                    "Compromiso de lectura sostenido",
                    "Precio más accesible por mes"
                  ].map((feature) => (
                    <div key={feature} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#C47F68] flex-shrink-0 mt-0.5" />
                      <span className="text-[#786C60]">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Button 
                  onClick={() => scrollToSection('registro')}
                  className="w-full btn-primary"
                  data-testid="price-quarterly-btn"
                >
                  Elegir plan trimestral
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Formas de Pago */}
          <div className="mt-16" data-testid="payment-methods">
            <h3 className="font-serif text-2xl text-[#4A4036] mb-8 text-center">Formas de pago</h3>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {/* Transferencia */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E6DCD3] text-center" data-testid="payment-transfer">
                <div className="w-14 h-14 bg-[#C47F68]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Banknote className="w-7 h-7 text-[#C47F68]" />
                </div>
                <p className="font-semibold text-[#4A4036] mb-2">Transferencia bancaria</p>
                <p className="text-sm text-[#786C60] mb-1">Alias</p>
                <p className="font-medium text-[#4A4036]">UN.ESPACIO.PROPIO</p>
                <p className="text-sm text-[#786C60] mt-2">Cuenta en Banco Supervielle</p>
              </div>

              {/* MercadoPago */}
              <a 
                href="https://link.mercadopago.com.ar/unespaciopropio" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white rounded-2xl p-6 shadow-sm border border-[#E6DCD3] text-center hover:border-[#00B1EA] hover:shadow-md transition-all block"
                data-testid="payment-mercadopago"
              >
                <div className="w-14 h-14 bg-[#00B1EA]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-7 h-7 text-[#00B1EA]" />
                </div>
                <p className="font-semibold text-[#4A4036] mb-2">MercadoPago</p>
                <p className="text-sm text-[#786C60] mb-3">Pagá con tarjeta, débito o saldo</p>
                <span className="inline-block bg-[#00B1EA]/10 text-[#00B1EA] text-sm font-medium px-4 py-1.5 rounded-full">
                  Pagar con MercadoPago
                </span>
              </a>

              {/* PayPal */}
              <a 
                href="https://www.paypal.me/AldanaCriscione" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-white rounded-2xl p-6 shadow-sm border border-[#E6DCD3] text-center hover:border-[#003087] hover:shadow-md transition-all block"
                data-testid="payment-paypal"
              >
                <div className="w-14 h-14 bg-[#003087]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-7 h-7 text-[#003087]" />
                </div>
                <p className="font-semibold text-[#4A4036] mb-2">PayPal</p>
                <p className="text-sm text-[#786C60] mb-3">Internacional (USD)</p>
                <span className="inline-block bg-[#003087]/10 text-[#003087] text-sm font-medium px-4 py-1.5 rounded-full">
                  Pagar con PayPal
                </span>
              </a>
            </div>

            {/* Comprobante - separado */}
            <div className="mt-10 max-w-xl mx-auto">
              <a 
                href="https://wa.link/ovk6r4" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex flex-col sm:flex-row items-center gap-5 bg-[#25D366]/5 rounded-2xl p-6 border-2 border-[#25D366]/30 hover:border-[#25D366] hover:bg-[#25D366]/10 transition-all"
                data-testid="payment-whatsapp"
              >
                <div className="w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <div className="text-center sm:text-left flex-1">
                  <p className="font-serif text-xl text-[#4A4036] font-semibold mb-1">Una vez que pagues, envianos el comprobante</p>
                  <p className="text-sm text-[#786C60]">Mandanos una foto o captura del comprobante de pago por WhatsApp para confirmar tu lugar</p>
                </div>
                <span className="inline-block bg-[#25D366] text-white text-sm font-semibold px-5 py-2.5 rounded-full flex-shrink-0">
                  Abrir WhatsApp
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Formulario de Registro */}
      <section id="registro" className="section-padding" data-testid="registration-section">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A4036] mb-4">¿Querés saber más? Conversemos</h2>
            <p className="text-lg text-[#786C60]">
              Dejá tus datos y te contactamos
            </p>
          </div>
          
          <Card className="card-soft" data-testid="registration-form-card">
            <CardContent className="p-8">
              {registrationSuccess ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-[#C47F68] rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="font-serif text-2xl text-[#4A4036] mb-4">¡Registro exitoso!</h3>
                  <p className="text-[#786C60] mb-6">
                    Te enviaremos un email con los datos de pago y los próximos pasos.
                  </p>
                  <Button 
                    onClick={() => setRegistrationSuccess(false)}
                    variant="outline"
                    className="btn-outline"
                  >
                    Registrar otro email
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-[#4A4036] mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      Nombre completo
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Tu nombre"
                      className="input-soft"
                      data-testid="input-name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#4A4036] mb-2">
                      <Mail className="w-4 h-4 inline mr-2" />
                      Email
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="tu@email.com"
                      className="input-soft"
                      data-testid="input-email"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-[#4A4036] mb-2">
                      <Phone className="w-4 h-4 inline mr-2" />
                      Teléfono / WhatsApp
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+54 9 11 1234-5678"
                      className="input-soft"
                      data-testid="input-phone"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={isSubmitting}
                    data-testid="submit-registration-btn"
                  >
                    {isSubmitting ? "Registrando..." : "Quiero ser parte"}
                    {!isSubmitting && <ArrowRight className="ml-2 w-5 h-5" />}
                  </Button>
                  
                  <p className="text-sm text-center text-[#786C60]">
                    Al registrarte, aceptás recibir información sobre el club de lectura.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Podcast Section */}

      {/* FAQ Section */}
      <section id="faq" className="section-padding bg-[#F5F0EB]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-[#4A4036] mb-4">Preguntas frecuentes</h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {[
              {
                question: "¿Necesito tener experiencia previa en literatura?",
                answer: "¡Para nada! No es necesario que tengas experiencia previa en estudios sobre literatura o talleres. Solo necesitás las ganas de leer y ser parte de una comunidad lectora. Ambos clubes están pensados para todos los niveles."
              },
              {
                question: "¿Qué diferencia hay entre el Club y el Club Liviano?",
                answer: "El Club incluye 3 tertulias mensuales y es una experiencia más intensiva de lectura y análisis. El Club Liviano es para quienes tienen menos tiempo: incluye 1 tertulia mensual, ideal para mantener el hábito de lectura de una manera más sostenible."
              },
              {
                question: "¿Cómo recibo el texto para leer?",
                answer: "¡Te lo enviamos nosotros! Una vez que te suscribís, recibís el texto del mes en formato digital por email, junto con la guía de lectura. No tenés que conseguir el libro por tu cuenta."
              },
              {
                question: "¿Cuándo son los encuentros?",
                answer: "El Club tiene 3 tertulias mensuales en el grupo de los Viernes (18:30 a 20 hs) — 2 cupos disponibles.\n\nEl Club Liviano tiene 1 tertulia mensual los Miércoles. La próxima es el 21 de Octubre, de 18:30 a 20:30 hs.\n\nTodos los encuentros quedan grabados por si no podés asistir en vivo."
              },
              {
                question: "¿Qué pasa si no puedo asistir a una tertulia?",
                answer: "No te preocupes. Todas las tertulias quedan grabadas y las compartimos en el grupo. Además, la comunidad de WhatsApp está activa toda la semana para que puedas participar cuando puedas."
              },
              {
                question: "¿Qué diferencia hay entre el plan mensual y trimestral del Club?",
                answer: "El plan trimestral incluye el acceso a 3 meses del Club de lectura con precio más accesible por mes ($48.000 vs $60.000). Además, te permite no solo comprometerte más sostenidamente con la lectura, sino asegurar tu lugar en el Club."
              },
              {
                question: "¿Cómo funciona la suscripción del Club Liviano?",
                answer: "El Club Liviano se paga por suscripción a través de MercadoPago. Una vez que te suscribís, se debita una vez por mes de forma automática. Podés cancelar cuando quieras, sin compromiso."
              },
              {
                question: "¿Desde qué país puedo participar?",
                answer: "¡De cualquier país! Los encuentros son virtuales y tenemos participantes de toda Latinoamérica y España. Para el pago, si estás fuera de Argentina podés usar PayPal en USD."
              }
            ].map((faq, index) => (
              <AccordionItem 
                key={faq.question} 
                value={faq.question}
                className="bg-white rounded-xl px-6 border border-[#E6DCD3]"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left text-[#4A4036] hover:text-[#C47F68] font-medium py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#786C60] pb-5 whitespace-pre-line">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* WhatsApp Grupo Cercano */}
      <section className="py-12 px-6 bg-[#FDFBF7]" data-testid="whatsapp-community">
        <div className="max-w-2xl mx-auto">
          <a 
            href="https://chat.whatsapp.com/K5MT9iGI2yn95xuB6qmrYo"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col sm:flex-row items-center gap-5 bg-[#25D366]/5 rounded-2xl p-6 border-2 border-[#25D366]/30 hover:border-[#25D366] hover:bg-[#25D366]/10 transition-all"
            data-testid="whatsapp-community-link"
          >
            <div className="w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-7 h-7 text-white" />
            </div>
            <div className="text-center sm:text-left flex-1">
              <p className="font-serif text-xl text-[#4A4036] font-semibold mb-1">Sumate a nuestro grupo cercano</p>
              <p className="text-sm text-[#786C60]">Recibí una breve dosis de literatura semanal directo en tu WhatsApp</p>
            </div>
            <span className="inline-block bg-[#25D366] text-white text-sm font-semibold px-5 py-2.5 rounded-full flex-shrink-0">
              Unirme al grupo
            </span>
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-[#4A4036] text-white" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6">
            ¿Lista para empezar a leer en comunidad?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Unite a Tertulias Literarias y descubrí el placer de leer acompañada, debatir ideas y compartir tu pasión por la literatura.
          </p>
          <Button 
            onClick={() => scrollToSection('registro')}
            className="bg-[#C47F68] hover:bg-[#B06E58] text-white rounded-full px-10 py-6 text-lg font-medium"
            data-testid="final-cta-btn"
          >
            Sumate al Club
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F0EB] py-12 px-6" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#C47F68] to-[#D4A574] rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-serif text-xl text-[#4A4036]">Un espacio propio</h3>
                <p className="text-sm text-[#786C60]">Tertulias literarias - El Club</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <a href="https://www.instagram.com/un.espacio.propio/" target="_blank" rel="noopener noreferrer" className="text-[#786C60] hover:text-[#C47F68] transition-colors">
                Instagram
              </a>
              <a href="https://wa.link/ovk6r4" target="_blank" rel="noopener noreferrer" className="text-[#786C60] hover:text-[#C47F68] transition-colors">
                WhatsApp
              </a>
              <a href="mailto:sol.criscione@gmail.com" className="text-[#786C60] hover:text-[#C47F68] transition-colors">
                Email
              </a>
            </div>
          </div>
          <div className="border-t border-[#E6DCD3] mt-8 pt-8 text-center">
            <p className="text-sm text-[#786C60]">
              © 2026 Un espacio propio - Tertulias Literarias. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
